// MASTER-PROJECT-WIZARD-INTEGRATION-001 (task section 3): the ONE shared
// frontend identity predicate for a canonical Pro Content Master V2
// record living in the normal `contents` collection — mirrors the
// backend's `core.master_project_content.is_canonical_master_content`
// exactly. Every component that needs to tell a canonical Master apart
// from a Social/Base content record must import this instead of
// re-declaring its own version of the check.

// A record is a canonical Master if and only if BOTH:
//   - origin === 'pro';
//   - its stored content_master is an object with version === 2.
// Never throws — a missing/malformed content field simply means "not
// canonical" (the caller's existing Social/Base path applies).
export function isCanonicalMasterContent(content) {
  if (!content || typeof content !== 'object') return false;
  if (content.origin !== 'pro') return false;
  const master = content.content_master;
  return Boolean(master) && typeof master === 'object' && master.version === 2;
}

// The main title to show for any content item, canonical-aware
// (MASTER-PROJECT-WIZARD-INTEGRATION-001A, task section 3): a Master's
// embedded `content_master.title` is the editorial source of truth and
// is always preferred; the top-level `title` compatibility field is
// only a defensive fallback for the rare shape where content_master is
// unexpectedly absent (never the reverse — a stale persisted `title`
// must never shadow the validated Master).
export function getContentDisplayTitle(content) {
  if (!content) return '';
  if (isCanonicalMasterContent(content)) {
    return content.content_master?.title || content.title || content.hook_text || '';
  }
  return content.hook_text || '';
}

// The supporting/secondary text: a Master's `content_master.primary_hook`
// (the source of truth) preferred over the `hook_text` compatibility
// fallback — same doctrine as getContentDisplayTitle above.
export function getContentDisplayHook(content) {
  if (!content) return '';
  if (isCanonicalMasterContent(content)) {
    return content.content_master?.primary_hook || content.hook_text || '';
  }
  return content.hook_text || '';
}

// The objective badge value for a canonical Master, preferring the
// embedded Master's own `objective` over the top-level compatibility
// field — `null` for non-Master content (callers decide their own
// objective/pillar presentation for Social content).
export function getContentObjective(content) {
  if (!content || !isCanonicalMasterContent(content)) return null;
  return content.content_master?.objective || content.objective || null;
}

// A stable, translatable "kind" discriminator for badges/icons —
// 'master' for a canonical Master, 'social' for everything else. Never
// 'format.null'/'format.undefined' — Master content's `format` is
// always `null` and must never be looked up in the `format.*` i18n
// namespace.
export function getContentKind(content) {
  return isCanonicalMasterContent(content) ? 'master' : 'social';
}
